import { z } from 'zod'

/**
 * Schémas de validation des formulaires.
 *
 * UNE SEULE définition, utilisée par le composant client ET par le Route
 * Handler côté serveur (docs/architecture.md § 4.5). La validation métier
 * définitive restera côté API en phase 9 : le cahier des charges impose une
 * double validation, front et back.
 *
 * Les messages d'erreur ne sont pas ici : ce sont des clés de traduction
 * résolues à l'affichage, depuis src/messages (libellés techniques).
 */

/**
 * Honeypot : champ invisible pour un humain, rempli par la plupart des robots.
 *
 * `startedAt` (horodatage d'ouverture) n'est volontairement PAS dans les schémas :
 * ce n'est pas une donnée utilisateur, mais un contrôle anti-spam lu directement
 * par le Route Handler.
 */
const antiSpam = {
  website: z.string().optional(),
}

/**
 * Les messages sont des CODES, pas du texte : ils sont traduits à l'affichage
 * depuis src/messages (`form.required`, `form.invalidEmail`…). Aucun texte
 * visible n'est écrit dans ce fichier.
 */
const email = z.string().trim().min(1, 'required').max(255, 'tooLong').email('invalidEmail')
const phone = z
  .string()
  .trim()
  .min(6, 'required')
  .max(30, 'tooLong')
  .regex(/^[+\d][\d\s().-]*$/, 'invalidPhone')
const shortText = z.string().trim().min(2, 'required').max(255, 'tooLong')
const longText = z.string().trim().min(10, 'required').max(5000, 'tooLong')
const consent = z.boolean().refine((value) => value === true, 'consentRequired')

export const contactSchema = z.object({
  fullName: shortText,
  company: z.string().trim().max(255).optional().or(z.literal('')),
  email,
  phone,
  subject: z.enum(['info', 'demo', 'autre'], 'required'),
  /** Slug du produit concerné. Facultatif. */
  product: z.string().trim().max(120).optional().or(z.literal('')),
  message: longText,
  consent,
  ...antiSpam,
})

export const partnerSchema = z.object({
  partnershipType: z.enum(['apporteur', 'integrateur', 'international'], 'required'),
  company: shortText,
  fullName: shortText,
  email,
  phone,
  country: shortText,
  message: longText,
  consent,
  ...antiSpam,
})

export const pfeApplicationSchema = z.object({
  /** Slug de l'offre, ou chaîne vide pour une candidature spontanée. */
  offer: z.string().trim().max(120).optional().or(z.literal('')),
  lastName: shortText,
  firstName: shortText,
  email,
  phone,
  school: shortText,
  speciality: shortText,
  desiredSubject: z.string().trim().max(255).optional().or(z.literal('')),
  message: longText,
  consent,
  ...antiSpam,
})

export type ContactInput = z.infer<typeof contactSchema>
export type PartnerInput = z.infer<typeof partnerSchema>
export type PfeApplicationInput = z.infer<typeof pfeApplicationSchema>

/** Contraintes sur le CV. La taille maximale sera pilotée par le back-office. */
export const CV_CONSTRAINTS = {
  maxSizeBytes: 5 * 1024 * 1024,
  extensions: ['.pdf', '.doc', '.docx'],
  mimeTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
}

/** Délai minimal entre l'ouverture du formulaire et son envoi, en millisecondes. */
export const MIN_FILL_DURATION_MS = 3000
